import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import { useState } from "react";
import { useUser } from "../contexts/UserContext";

interface UserProfile {
  fullName: string;
  userName: string;
  email: string;
  password: string;
  address: string;
  phone: string;
  image: string;
}

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const noNavRoutes = ["/login", "/signup", "/forgot-password"];
  const showNavBar = !noNavRoutes.includes(location.pathname);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // use global user - local profile is kept for backwards compatibility
  const { user, setUser } = useUser();
  const [profile, setProfile] = useState<UserProfile | undefined>(undefined);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate("/profile");
  };
  const handleEditVenuesClick = () => {
    setAnchorEl(null);
    navigate("/edit-venues");
  };
  const handleAllDataClick = () => {
    setAnchorEl(null);
    navigate("/all-data");
  };
  const handleLogout = () => {
    setAnchorEl(null);
    setUser(null);
    localStorage.clear();
    navigate("/login");
  };

  // Mirror context user -> profile for local display
  useEffect(() => {
    if (user) {
      setProfile({
        fullName: user.name || "",
        userName: user.name || "",
        email: user.email || "",
        password: "********",
        address: user.address || "",
        phone: user.mobile ? `${user.country_code || ""} ${user.mobile}` : "",
        image: user.banner || "",
      });
      setLoading(false);
    }
  }, [user]);

  return (
    <>
      {showNavBar && (
        <AppBar position="static" color="primary" sx={{ mb: { xs: 1, sm: 2 } }}>
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 1, sm: 2 },
              minHeight: { xs: 48, sm: 64 },
            }}
          >
            <Typography
              variant="h6"
              component="div"
              onClick={() => navigate("/")}
              sx={{
                flexGrow: 1,
                fontSize: { xs: "1rem", sm: "1.25rem" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              Dashboard
            </Typography>
            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={handleAvatarClick}
                sx={{
                  ml: 1,
                  p: 0,
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                }}
              >
                <Avatar
                  src={profile?.image || ""}
                  alt={profile?.fullName || "Profile"}
                  sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}
                />
              </IconButton>
            </div>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  width: { xs: 160, sm: 200 },
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                },
              }}
            >
              <MenuItem
                onClick={handleProfileClick}
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                Profile
              </MenuItem>
              <MenuItem
                onClick={handleEditVenuesClick}
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                Edit Venues
              </MenuItem>
              <MenuItem
                onClick={handleAllDataClick}
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                All Data
              </MenuItem>
              <MenuItem
                onClick={handleLogout}
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
      )}
      <Container
        sx={{
          padding: 0,
          margin: 0,
          maxWidth: "100vw !important",
          minWidth: 0,
          width: "100vw",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
        disableGutters
      >
        {children}
      </Container>
      <style>{`
        @media (max-width: 600px) {
          body, #root {
            width: 100vw;
            overflow-x: hidden;
          }
        }
      `}</style>
    </>
  );
};

export default MainLayout;
